import React from 'react';
import Modal from './Modal';
import { AttachmentVoidControl } from '../hooks/useAttachmentVoid';
import './AttachmentVoid.css';

interface AttachmentVoidModalProps<T> {
  control: AttachmentVoidControl<T>;
  /** Stack above another open modal, e.g. voiding a file inside an edit dialog. */
  elevated?: boolean;
}

/**
 * Collects the reason a file is being set aside. Voiding keeps the file, so the
 * copy says so — the reason is what someone reading the record later needs.
 */
function AttachmentVoidModal<T>({ control, elevated = false }: AttachmentVoidModalProps<T>) {
  const { noun, target, targetLabel, reason, setReason, error, busy, cancel, confirm } = control;
  const inputId = `void-${noun.replace(/\s+/g, '-')}-reason`;

  return (
    <Modal
      isOpen={target != null}
      onClose={cancel}
      title={`Void this ${noun}`}
      narrow
      elevated={elevated}
    >
      <div className="modal-body">
        <p className="attachment-void-explainer">
          {targetLabel ? (
            <>
              <strong>{targetLabel}</strong> stays on the record with your reason attached, and can be restored later.
            </>
          ) : (
            <>The file stays on the record with your reason attached, and can be restored later.</>
          )}
        </p>
        <div className="form-group">
          <label htmlFor={inputId} className="form-label">
            Reason for voiding
          </label>
          <textarea
            id={inputId}
            className="form-textarea"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Wrong photo — uploaded to the wrong appointment"
            disabled={busy}
            autoFocus
          />
        </div>
        {error ? (
          <p className="create-order-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
      <div className="modal-actions">
        <button type="button" className="cancel-button" disabled={busy} onClick={cancel}>
          Cancel
        </button>
        <button type="button" className="attachment-void-confirm" disabled={busy} onClick={confirm}>
          {busy ? 'Voiding…' : `Void ${noun}`}
        </button>
      </div>
    </Modal>
  );
}

export default AttachmentVoidModal;
